import React, {
  useEffect, useRef,
} from 'react';
import { SearchBox, withSearch } from '@elastic/react-search-ui';
import { useJobListingContext } from '@scripts/frontend/job-listing/JobListingContext';
import DynamicVideo from '@scripts/frontend/components/DynamicVideo';
import SearchClearButton from './SearchClearButton';
import { useMobileSearch } from '../mobile/MobileSearchContext';

interface Props {
  searchTerm: string;
  setSearchTerm: (t: string, opts?: any) => void;
}
const SearchHeaderImpl: React.FC<Props> = ({ searchTerm, setSearchTerm }) => {
  const { headerData } = useJobListingContext();
  const videoRef = useRef<HTMLVideoElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const highlightIndexRef = React.useRef<number | null>(null);
  const itemsRef = React.useRef<any[]>([]);
  const {
    isMobile, open, openSearch, closeSearch,
  } = useMobileSearch();

  // helper to focus the input
  const focusInput = () => inputRef.current?.focus();

  // close on ESC when open
  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => e.key === 'Escape' && closeSearch();
    if (open) document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [open, closeSearch]);

  // This useEffect reliably focuses the input when the search opens on mobile.
  useEffect(() => {
    if (open && isMobile) {
      // requestAnimationFrame ensures the focus happens after the UI updates.
      requestAnimationFrame(focusInput);
    }
  }, [open, isMobile]);

  const handleFocus = () => {
    if (isMobile && !open) {
      openSearch();
      return;
    }
    // desktop-only video behavior
    if (videoRef.current && (videoRef.current.ended || videoRef.current.paused)) {
      videoRef.current.currentTime = 0;
      videoRef.current.play();
      videoRef.current.loop = true;
    }
  };

  const handleBlur = () => {
    if (!isMobile && videoRef.current) videoRef.current.loop = false;
  };

  const scrollToJobListing = () => {
    document.getElementById('job-listing')?.scrollIntoView({ behavior: 'smooth' });
    closeSearch();
  };

  return (
    <div className={`search-header ${open ? 'mobile-search-active' : ''}`}>
      <div className="search-header__container">
        {/* desktop video only */}
        {!isMobile && (
          <div className="search-header__video-wrapper">
            <DynamicVideo
              ref={videoRef}
              mp4Src={headerData.video_mp4}
              webmSrc={headerData.video_webm}
              poster={headerData.poster_image}
              className="search-header__video"
              videoProps={{ playsInline: true, muted: true }}
            />
          </div>
        )}
        <div className="search-header__input-wrapper">
          <span className="search-header__icon-search">
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="29" viewBox="0 0 28 29" fill="none"><path
              d="M17.4588 2.99499C15.5265 1.06277 12.9541 -0.000518799 10.2215 -0.000518799C7.48897 -0.000518799 4.91649 1.06277 2.98428 2.99499C-0.194161 6.17343 -0.925888 11.1583 1.23499 15.1485L1.62372 15.8231L1.74949 16.0403C1.80665 16.1432 1.88669 16.2004 2.01245 16.2347L3.16721 16.5434C3.31584 16.5777 3.43017 16.5091 3.46447 16.3719L3.77317 15.2171C3.80747 15.1028 3.7846 14.9999 3.72744 14.897L3.60167 14.6798L3.22438 14.0395C1.56656 10.9754 2.12679 7.09952 4.59636 4.62994C6.09412 3.13219 8.09493 2.29756 10.2215 2.29756C12.3481 2.29756 14.3375 3.12075 15.8352 4.62994C18.9336 7.72835 18.9336 12.7704 15.8352 15.8802C13.3657 18.3498 9.50122 18.91 6.45998 17.2751L5.85402 16.9207L5.56819 16.7492C5.46529 16.692 5.36239 16.6806 5.24806 16.7034L4.0933 17.0007C3.94467 17.035 3.87607 17.1608 3.9218 17.298L4.21907 18.4527C4.25337 18.5671 4.31053 18.6585 4.41343 18.7157L4.63066 18.8414L5.33952 19.253C6.86014 20.0762 8.5294 20.4764 10.1872 20.4764C12.8512 20.4764 15.4922 19.4474 17.4473 17.4923C21.4375 13.5021 21.4375 7.00805 17.4473 3.01786L17.4588 2.99499Z"
              fill="black"></path><path
              d="M20.2941 18.8414L20.1111 18.6585C20.0311 18.5785 19.9282 18.5327 19.8139 18.5327H18.6248C18.4762 18.5327 18.3733 18.6242 18.3733 18.7843V19.9733C18.3733 20.0991 18.419 20.1906 18.4991 20.2706L18.682 20.4535L26.388 28.1595L28.0001 26.5474L20.2941 18.8414Z"
              fill="black"></path></svg>
          </span>

          <SearchBox
            autocompleteMinimumCharacters={3}
            autocompleteResults={{
              sectionTitle: 'Results',
              titleField: 'job_title',
              shouldTrackClickThrough: true,
            }}
            autocompleteSuggestions
            searchAsYouType
            debounceLength={300}
            inputProps={{
              ref: inputRef,
              placeholder: 'Search jobs and keywords',
              className: `search-header__input ${searchTerm ? 'has-value' : ''}`,
              onFocus: handleFocus,
              onBlur: handleBlur,
              onClick: () => {
                if (isMobile && !open) {
                  openSearch();
                }
              },
              inputMode: 'search',
              autoCorrect: 'off',
              autoCapitalize: 'none',
              // : Enter selects the current highlighted item
              onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === 'Enter' && highlightIndexRef.current != null) {
                  const item = itemsRef.current[highlightIndexRef.current];
                  if (item) {
                    const val = item?.job_title?.raw
                      ?? item?.job_title?.snippet?.[0]?.replace(/<\/?em>/g, '')
                      ?? (typeof item === 'string' ? item : '');
                    if (val) {
                      setSearchTerm(val, { shouldClearFilters: true });
                      scrollToJobListing();
                      e.preventDefault();
                    }
                  }
                }
              },
            }}
            /* Keyboard-accessible custom dropdown */
            autocompleteView={({
              autocompletedResults,
              autocompletedSuggestions,
              autocompleteResults,
              autocompleteSuggestions,
              getItemProps,
              getMenuProps,
              highlightedIndex,
              isOpen,
            }) => {
              const results = Array.isArray(autocompletedResults)
                ? autocompletedResults
                : Array.isArray(autocompleteResults)
                  ? autocompleteResults
                  : [];
              const suggestions = Array.isArray(autocompletedSuggestions)
                ? autocompletedSuggestions
                : Array.isArray(autocompleteSuggestions)
                  ? autocompleteSuggestions
                  : [];

              // Deduplicate job titles
              const newJobtitle = new Set<string>();
              const jobTitleResults = results.filter((res) => {
                const rawTitle = res.job_title?.raw ?? res.job_title?.snippet?.[0] ?? '';
                const title = rawTitle.replace(/<\/?em>/g, '').trim().toLowerCase().normalize('NFC');
                if (!title) return false;
                if (newJobtitle.has(title)) return false;
                newJobtitle.add(title);
                return true;
              });

              const flatItems = [
                ...jobTitleResults,
                ...suggestions.map((s) => (typeof s === 'string' ? s : s.suggestion)),
              ];
              itemsRef.current = flatItems;
              highlightIndexRef.current = typeof highlightedIndex === 'number' ? highlightedIndex : null;

              if (!isOpen) return null;

              return (
                <ul
                  className="sui-search-box__results-list autocomplete-list"
                  {...getMenuProps({ role: 'listbox', id: 'jobs-ac' })}
                >
                  {/* Results (deduplicates the job titles) */}
                  {jobTitleResults.map((res: any, rIdx: number) => {
                    const html = res.job_title?.snippet?.[0]
                      ?? (res.job_title?.raw
                        ? res.job_title.raw.replace(/&/g, '&amp;').replace(/</g, '&lt;')
                        : '');
                    return (
                      <li
                        {...getItemProps({
                          item: res,
                          index: rIdx,
                          key: res.id || `res-${rIdx}`,
                          role: 'option',
                          id: `jobs-ac-item-${rIdx}`,
                          'aria-selected': highlightedIndex === rIdx,
                        })}
                        className={`autocomplete-item ${
                          highlightedIndex === rIdx ? 'highlight' : ''
                        }`}
                      >
                        <span dangerouslySetInnerHTML={{ __html: html }} />
                      </li>
                    );
                  })}
                  {/* Suggestions */}
                  {suggestions.map((s: any, sIdx: number) => {
                    const idx = jobTitleResults.length + sIdx;
                    const label = typeof s === 'string' ? s : s.suggestion;
                    return (
                      <li
                        {...getItemProps({
                          item: label,
                          index: idx,
                          key: `sugg-${sIdx}`,
                          role: 'option',
                          id: `jobs-ac-item-${idx}`,
                          'aria-selected': highlightedIndex === idx,
                        })}
                        className={`autocomplete-item ${
                          highlightedIndex === idx ? 'highlight' : ''
                        }`}
                      >
                        {label}
                      </li>
                    );
                  })}
                </ul>
              );
            }}
            // Click or Downshift selection path
            onSelectAutocomplete={(selection: any) => {
              const val = selection?.job_title?.snippet?.[0]?.replace(/<\/?em>/g, '')
                ?? selection?.job_title?.raw
                ?? (typeof selection === 'string' ? selection : '');
              if (typeof val === 'string' && val) {
                setSearchTerm(val, { shouldClearFilters: true });
                scrollToJobListing();
              }
            }}
            onSubmit={() => scrollToJobListing()}
          />
          <SearchClearButton searchTerm={searchTerm} setSearchTerm={setSearchTerm}/>
        </div>
      </div>
      {open && (
        <button
          className="search-header__mobile-close"
          aria-label="Close search"
          onClick={closeSearch}
        >
        </button>
      )}
    </div>
  );
};
export default withSearch(({ searchTerm, setSearchTerm }) => ({
  searchTerm,
  setSearchTerm,
}))(SearchHeaderImpl);
